import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Progress } from "@/components/ui/progress";
import { toast } from "sonner";
import {
  ArrowLeft, Award, Sprout, Star, Medal, Trophy, Crown, Gem,
  BookOpen, Library, GraduationCap, Flame, Lock
} from "lucide-react";
import { API, useAuth, authFetch } from "@/App";

const ICONS = {
  sprout: Sprout,
  star: Star,
  medal: Medal,
  trophy: Trophy,
  crown: Crown,
  gem: Gem,
  "book-open": BookOpen,
  library: Library,
  "graduation-cap": GraduationCap,
  flame: Flame,
};

const TIER_COLORS = {
  bronze: { ring: "ring-amber-700/40", bg: "bg-amber-700/15", text: "text-amber-500" },
  silver: { ring: "ring-slate-300/40", bg: "bg-slate-300/15", text: "text-slate-200" },
  gold: { ring: "ring-gold-400/60", bg: "bg-gold-400/15", text: "text-gold-400" },
};

export default function Badges() {
  const navigate = useNavigate();
  useAuth();
  const [data, setData] = useState(null);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const res = await authFetch(`${API}/progress/badges`);
        if (res.ok) setData(await res.json());
        else toast.error("Failed to load badges");
      } catch (e) {
        toast.error("Failed to load badges");
      } finally {
        setIsLoading(false);
      }
    })();
  }, []);

  if (isLoading) {
    return (
      <div className="min-h-screen bg-islamic-blue-950 flex items-center justify-center">
        <div className="spinner w-12 h-12"></div>
      </div>
    );
  }

  const earned = data?.badges?.filter((b) => b.earned) || [];
  const locked = data?.badges?.filter((b) => !b.earned) || [];

  return (
    <div data-testid="badges-page" className="min-h-screen bg-islamic-blue-950">
      <header className="glass border-b border-white/5 sticky top-0 z-50">
        <div className="max-w-4xl mx-auto px-6 py-4 flex items-center justify-between">
          <button
            data-testid="back-btn"
            onClick={() => navigate("/dashboard")}
            className="flex items-center gap-2 text-slate-400 hover:text-gold-400 transition-colors font-outfit"
          >
            <ArrowLeft className="w-5 h-5" />
            <span>Back</span>
          </button>
          <div className="flex items-center gap-3">
            <Award className="w-5 h-5 text-gold-400" />
            <h1 className="font-cinzel text-lg font-semibold text-slate-100">Achievements</h1>
          </div>
          <div className="w-16"></div>
        </div>
      </header>

      <main className="max-w-4xl mx-auto px-6 py-8">
        {/* Summary card */}
        <section
          data-testid="achievements-summary"
          className="glass-card rounded-2xl p-6 mb-8 relative overflow-hidden"
        >
          <div
            aria-hidden
            className="absolute -top-12 -right-12 w-48 h-48 rounded-full bg-gold-400/10 blur-3xl"
          />
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 relative">
            <div>
              <p className="text-slate-500 text-xs font-outfit uppercase tracking-wider">Rank</p>
              <p className="font-cinzel text-2xl text-gold-400 mt-1">{data?.rank || "Beginner"}</p>
            </div>
            <div>
              <p className="text-slate-500 text-xs font-outfit uppercase tracking-wider">Points</p>
              <p data-testid="points-value" className="font-cinzel text-2xl text-slate-100 mt-1">
                {data?.points?.toLocaleString() || 0}
              </p>
            </div>
            <div>
              <p className="text-slate-500 text-xs font-outfit uppercase tracking-wider">Earned</p>
              <p data-testid="earned-count" className="font-cinzel text-2xl text-slate-100 mt-1">
                {data?.earned_count || 0}
                <span className="text-slate-500 text-base">/{data?.total_count || 0}</span>
              </p>
            </div>
            <div>
              <p className="text-slate-500 text-xs font-outfit uppercase tracking-wider">Memorized</p>
              <p className="font-cinzel text-2xl text-slate-100 mt-1">
                {data?.metrics?.ayahs || 0}
                <span className="text-slate-500 text-base"> ayahs</span>
              </p>
            </div>
          </div>
        </section>

        {earned.length > 0 && (
          <section className="mb-10">
            <h2 className="font-cinzel text-lg text-slate-100 mb-4">Earned</h2>
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {earned.map((b) => (
                <BadgeCard key={b.id} badge={b} />
              ))}
            </div>
          </section>
        )}

        {locked.length > 0 && (
          <section>
            <h2 className="font-cinzel text-lg text-slate-100 mb-4">In Progress</h2>
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {locked.map((b) => (
                <BadgeCard key={b.id} badge={b} />
              ))}
            </div>
          </section>
        )}

        <div className="text-center mt-10">
          <Button
            data-testid="continue-memorizing-btn"
            onClick={() => navigate("/dashboard")}
            className="btn-gold rounded-full px-6"
          >
            Continue Memorizing
          </Button>
        </div>
      </main>
    </div>
  );
}

function BadgeCard({ badge }) {
  const Icon = ICONS[badge.icon] || Award;
  const tier = TIER_COLORS[badge.tier] || TIER_COLORS.bronze;
  const earned = badge.earned;

  return (
    <div
      data-testid={`badge-${badge.id}`}
      className={`glass-card rounded-xl p-5 transition-all ${
        earned ? `ring-1 ${tier.ring}` : "opacity-80"
      }`}
    >
      <div className="flex items-start gap-4">
        <div
          className={`w-14 h-14 rounded-2xl flex items-center justify-center ${
            earned ? tier.bg : "bg-islamic-blue-800/60"
          }`}
        >
          {earned ? (
            <Icon className={`w-7 h-7 ${tier.text}`} />
          ) : (
            <Lock className="w-6 h-6 text-slate-500" />
          )}
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2">
            <h3 className="font-outfit font-semibold text-slate-100 truncate">{badge.name}</h3>
            <span
              className={`text-[10px] uppercase tracking-wider font-outfit px-1.5 py-0.5 rounded ${
                earned ? tier.bg + " " + tier.text : "bg-slate-700/40 text-slate-500"
              }`}
            >
              {badge.tier}
            </span>
          </div>
          <p className="text-slate-400 text-sm font-outfit mt-1">{badge.description}</p>
          {!earned && (
            <div className="mt-3">
              <Progress value={badge.progress_pct} className="h-1.5 bg-islamic-blue-800" />
              <p className="text-slate-500 text-xs font-outfit mt-1.5">
                {badge.progress} / {badge.threshold}
              </p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
